<template>
  <div
    v-loading="showLoader"
    el-loading-background="fixed-white"
    el-loading-color="brand-secondary"
    el-loading-size="48"
    :el-loading-text="loadingText"
  >
    <div class="pa-24">
      <strong>
        {{ $t('delete-account.subscriptions-check-step.licenses-heading') }}
      </strong>
      <div>
        {{ $t('delete-account.subscriptions-check-step.licenses-info') }}
      </div>
      <el-input
        v-if="licenses.length"
        v-model="licenses"
        type="textarea"
        class="mt-12"
        readonly
        resize="none"
        :autosize="{minRows: 2, maxRows: 5}"
      />
      <div
        v-else
        class="mt-16 el-text el-text--strong"
      >
        {{ noLicensesFound }}
      </div>
    </div>
    <el-divider />
    <div class="pa-24">
      <strong>
        {{ $t('delete-account.subscriptions-check-step.data-heading') }}
      </strong>
      <div>
        {{ $t('delete-account.subscriptions-check-step.data-info') }}
      </div>
      <el-link
        href="ui/#/backup-console/backups/vaults-locations"
        target="blank"
      >
        {{ $t('delete-account.subscriptions-check-step.cloud-storage', { storageUsage: convertedUsage }) }}
      </el-link>
    </div>
    <el-divider />
    <el-checkbox
      class="pa-24"
      :checked="acknowledgementChecked"
      multiline-label
      @change="handleCheckboxChange"
    >
      {{ $t('delete-account.subscriptions-check-step.data-acknowlegement') }}
    </el-checkbox>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'SubscriptionsCheckStep',
  emits: ['checkbox-change'],
  data() {
    return {
      acknowledgementChecked: false
    };
  },
  computed: {
    ...mapGetters('app', [
      'isTenantLicensesFetching',
      'isTenantStorageUsageFetching',
      'tenantLicenses',
      'tenantStorageUsage'
    ]),
    loadingText() {
      return this.$t('delete-account.subscriptions-check-step.loading-text');
    },
    licenses() {
      return Object.values(
        this.tenantLicenses
          .filter((license) => !license.is_auto_renewal_enabled)
          .reduce((uniqueLicenses, license) => this.addLicensesCount(uniqueLicenses, license), {})
        )
        .map((license) => this.composeLicenseName(license))
        .join('\n');
    },
    renewals() {
      return Object.values(
        this.tenantLicenses
          .filter((license) => license.is_auto_renewal_enabled)
          .reduce((acc, license) => this.addLicensesCount(acc, license), {})
        )
        .map((license) => this.composeLicenseName(license))
        .join('\n');
    },
    showLoader() {
      return this.isTenantLicensesFetching || this.isTenantStorageUsageFetching;
    },
    convertedUsage() {
      const oneGB = 1073741824;
      const oneTB = 1099511627776;
      return this.tenantStorageUsage < oneTB
        ? `${this.tenantStorageUsage / oneGB} GB`
        : `${(this.tenantStorageUsage / oneTB).toPrecision(
            Number.isInteger(this.tenantStorageUsage / oneTB) ? 1 : 2
          )} TB`;
    },
    noLicensesFound() {
      return this.$t('delete-account.subscriptions-check-step.no-licenses');
    }
  },
  async created() {
    this.fetchTenantLicenses();
    this.fetchTenantStorageUsage();
  },
  methods: {
    ...mapActions('app', ['fetchTenantLicenses', 'fetchTenantStorageUsage']),
    handleCheckboxChange(checked) {
      this.acknowledgementChecked = checked;
      this.$emit('checkbox-change', this.acknowledgementChecked);
    },
    addLicensesCount(uniqueLicenses, license) {
      if (uniqueLicenses[license.bundle_id]) {
        uniqueLicenses[license.bundle_id].count += 1;
      } else {
        uniqueLicenses[license.bundle_id] = { ...license, count: 1 };
      }
      return uniqueLicenses;
    },
    composeLicenseName(license) {
      return (license.count === 1 ? `● ${license.title}` : `● ${license.title} (${license.count})`);
    }
  }
};
</script>
